import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "react-bootstrap/Button";
import Dropdown from "react-bootstrap/Dropdown";

import T, { GetLang } from "@components/translations/Translations";
import { GUARDIAN_DASHBOARD } from "@utils/auth/config";
import { logout } from "@utils/auth/Auth";

// ─── Module-level helpers ─────────────────────────────────────────────────────

/**
 * The forwardRef is important!!
 * Dropdown needs access to the DOM node in order to position the Menu
 */
const HamburgerToggle = React.forwardRef<
    HTMLButtonElement,
    { children?: React.ReactNode; onClick?: React.MouseEventHandler }
>(({ children, onClick }, ref) => (
    <Button
        active={false}
        bsPrefix="btn"
        className="btn btn-small btn-transparent btn-outline"
        ref={ref}
        onClick={(e) => {
            e.preventDefault();
            onClick?.(e);
        }}
        size="sm"
        type="button"
        variant="outline-light"
    >
        {children}
    </Button>
));
HamburgerToggle.displayName = "HamburgerToggle";

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * @name Hamburger
 * @return {JSX.Element}
 */
const Hamburger = (): JSX.Element => {
    const location = useLocation();
    const navigate = useNavigate();
    const lang = GetLang();

    const dashboard = (): void => {
        if (location.pathname.includes("archive")) {
            navigate("/home");
        } else {
            globalThis.location.href = GUARDIAN_DASHBOARD;
        }
    };
    const handleFaqClick = (): void => {
        globalThis.location.assign("https://www.dcsdk12.org/about/departments/choice-programming/open-enrollment");
    };

    const dashboardKeyDown = (e: React.KeyboardEvent): void => {
        if (e.key?.toLowerCase() === "enter") dashboard();
    };

    return (
        <Dropdown align="end">
            <Dropdown.Toggle as={HamburgerToggle} id="dropdown-custom-components">
                <i className="bi bi-list" style={{ color: "#19608f", fontSize: "2.0rem", fontWeight: "bold" }} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
                {location.pathname.includes("archive") ||
                    (location.pathname.includes("open-enrollment") && (
                        <>
                            <Dropdown.Item href="#">
                                <button
                                    aria-label="FAQ Button"
                                    className="btn btn-primary color-override"
                                    onClick={handleFaqClick}
                                    onKeyDown={handleFaqClick}
                                    style={{ width: "100%" }}
                                    type="button"
                                >
                                    <span className="nav-button-text">
                                        {T({ tKey: "Global_Faq", langStr: lang })}
                                        <i className="bi bi-person-raised-hand" style={{ marginLeft: "5px" }} />
                                    </span>
                                </button>
                            </Dropdown.Item>
                            <Dropdown.Item href="#">
                                <button
                                    aria-label="Dashboard Button"
                                    className="btn btn-primary color-override"
                                    onClick={dashboard}
                                    onKeyDown={dashboardKeyDown}
                                    style={{ width: "100%" }}
                                    type="button"
                                >
                                    <span className="nav-button-text">
                                        {T({ tKey: "Global_Dashboard", langStr: lang })}
                                        <i className="bi bi-house" style={{ marginLeft: "5px" }} />
                                    </span>
                                </button>
                            </Dropdown.Item>
                        </>
                    ))}
                {!location.pathname.includes("open-enrollment") && (
                    <Dropdown.Item href="#">
                        <button
                            aria-label="Logout Button"
                            className="btn btn-primary color-override"
                            onClick={logout}
                            onKeyDown={logout}
                            style={{ width: "100%" }}
                            type="button"
                        >
                            <span className="nav-button-text">
                                {T({ tKey: "Main_Nav_LogOut", langStr: lang })}
                                <i className="bi bi-box-arrow-left" style={{ marginLeft: "5px" }} />
                            </span>
                        </button>
                    </Dropdown.Item>
                )}
                <Dropdown.Header>
                    {/* Helpful Links */}
                    <span style={{ color: "#333", fontSize: "1.1rem" }}>
                        {T({ tKey: "Main_Nav_HelpfulLinks", langStr: lang })}
                    </span>
                    <hr style={{ margin: "0 0 -2px 0" }} />
                </Dropdown.Header>
                <Dropdown.Item href="https://www.dcsdk12.org/schools-academics/academic-calendars" target="_blank">
                    {/* Academic Calendar */}
                    <i className="bi bi-calendar2-week" /> {T({ tKey: "Main_Nav_HelpfulLinks_LI_s2", langStr: lang })}
                </Dropdown.Item>
                <Dropdown.Item href="https://dcsd.nutrislice.com/menus-eula" target="_blank">
                    {/* Lunch Menus */}
                    <i className="bi bi-apple" /> {T({ tKey: "Main_Nav_HelpfulLinks_LI_s6", langStr: lang })}
                </Dropdown.Item>
                <Dropdown.Item href="https://www.myschoolbucks.com/ver2/getmain?requestAction=home" target="_blank">
                    {/* Online Payments */}
                    <i className="bi bi-coin" /> {T({ tKey: "Main_Nav_HelpfulLinks_LI_s5", langStr: lang })}
                </Dropdown.Item>
                <Dropdown.Item href="https://www.dcsdk12.org/parents-students/resources" target="_blank">
                    {/* Parent Resources */}
                    <i className="bi bi-journals" /> {T({ tKey: "Main_Nav_HelpfulLinks_LI_s3", langStr: lang })}
                </Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
    );
};

export default Hamburger;
